import { useState } from 'react';
import { X } from 'lucide-react';
import { Toggle } from '@/components/ui';

/**
 * Werte, die ein BabyNameForm produziert. Sie bilden 1:1 die schreibbaren
 * Spalten der Tabelle `baby_names` ab.
 *
 * Pflichtfelder (name, gender, origin, locale) entsprechen den NOT-NULL-Spalten
 * der Tabelle. Insbesondere `origin` ist NOT NULL OHNE Default — wird es nicht
 * mitgeliefert, bricht der Insert mit Postgres 23502 (genau der Bug, der das
 * Genehmigen von Community-Vorschlägen lahmgelegt hat). Deshalb wird origin hier
 * client-seitig erzwungen.
 *
 * `gender` verwendet konsequent 'neutral' (nicht 'unisex') — passend zum
 * CHECK-Constraint von baby_names/name_suggestions.
 */
export interface BabyNameFormValues {
  name: string;
  gender: 'male' | 'female' | 'neutral';
  origin: string;
  meaning: string | null;
  popularity_rank: number | null;
  pronunciation: string | null;
  nicknames: string[] | null;
  similar_names: string[] | null;
  is_active: boolean;
  locale: string;
}

interface BabyNameFormProps {
  /** Titel im Modal-Header (z.B. "Name hinzufügen", "Vorschlag genehmigen"). */
  title: string;
  /** Vorbefüllung für Bearbeiten/Genehmigen. Nicht gesetzte Felder bleiben leer. */
  initialValues?: Partial<BabyNameFormValues>;
  /** Aufrufer übernimmt die DB-Logik (Insert/Update). Diese Komponente ist reine UI. */
  onSubmit: (values: BabyNameFormValues) => void;
  onCancel: () => void;
  submitLabel: string;
  isLoading?: boolean;
}

const inputClass =
  'w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500';

/** Komma-getrennte Eingabe → getrimmtes string[] ohne leere Einträge (oder null). */
function parseList(raw: string): string[] | null {
  const items = raw
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
  return items.length > 0 ? items : null;
}

/**
 * Gemeinsames Formular für alle drei Konsumenten:
 * - Genehmigen-Pop-up (NameSuggestionsPage)
 * - Name hinzufügen (NamesPage)
 * - Name bearbeiten (NamesPage)
 *
 * Rendert sich selbst als Overlay-Modal (Pattern aus features/users/UserDetailModal).
 * Enthält bewusst KEINE Supabase-Logik — der Aufrufer entscheidet, ob die Werte
 * per Insert oder Update landen.
 */
export function BabyNameForm({
  title,
  initialValues,
  onSubmit,
  onCancel,
  submitLabel,
  isLoading,
}: BabyNameFormProps) {
  const [name, setName] = useState(initialValues?.name ?? '');
  const [gender, setGender] = useState<BabyNameFormValues['gender']>(
    initialValues?.gender ?? 'neutral'
  );
  const [origin, setOrigin] = useState(initialValues?.origin ?? '');
  const [meaning, setMeaning] = useState(initialValues?.meaning ?? '');
  const [popularityRank, setPopularityRank] = useState(
    initialValues?.popularity_rank != null ? String(initialValues.popularity_rank) : ''
  );
  const [pronunciation, setPronunciation] = useState(initialValues?.pronunciation ?? '');
  const [nicknames, setNicknames] = useState((initialValues?.nicknames ?? []).join(', '));
  const [similarNames, setSimilarNames] = useState(
    (initialValues?.similar_names ?? []).join(', ')
  );
  const [isActive, setIsActive] = useState(initialValues?.is_active ?? true);
  const [locale, setLocale] = useState(initialValues?.locale ?? 'de');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // origin ist NOT NULL ohne Default → vor dem Insert hart absichern.
    if (!origin.trim()) return;

    const parsedRank = parseInt(popularityRank, 10);

    onSubmit({
      name: name.trim(),
      gender,
      origin: origin.trim(),
      meaning: meaning.trim() || null,
      popularity_rank: Number.isNaN(parsedRank) ? null : parsedRank,
      pronunciation: pronunciation.trim() || null,
      nicknames: parseList(nicknames),
      similar_names: parseList(similarNames),
      is_active: isActive,
      locale,
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50" style={{ backdropFilter: 'blur(2px)' }}>
      <div className="fixed right-0 top-0 h-full w-full max-w-xl bg-white shadow-xl overflow-y-auto">
        <form onSubmit={handleSubmit} className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-slate-900">{title}</h2>
            <button
              type="button"
              onClick={onCancel}
              className="p-2 hover:bg-slate-100 rounded-lg"
            >
              <X className="w-6 h-6 text-slate-500" />
            </button>
          </div>

          <div className="space-y-4">
            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Name *</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                placeholder="z.B. Emma"
                className={inputClass}
              />
            </div>

            {/* Gender */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Geschlecht *</label>
              <select
                value={gender}
                onChange={(e) => setGender(e.target.value as BabyNameFormValues['gender'])}
                required
                className={inputClass}
              >
                <option value="male">Männlich</option>
                <option value="female">Weiblich</option>
                <option value="neutral">Neutral</option>
              </select>
            </div>

            {/* Origin (Pflicht) */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Herkunft *</label>
              <input
                type="text"
                value={origin}
                onChange={(e) => setOrigin(e.target.value)}
                required
                placeholder="z.B. german, greek, hebrew"
                className={inputClass}
              />
            </div>

            {/* Meaning */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Bedeutung</label>
              <input
                type="text"
                value={meaning}
                onChange={(e) => setMeaning(e.target.value)}
                placeholder="z.B. Die Allumfassende"
                className={inputClass}
              />
            </div>

            {/* Popularity Rank */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Beliebtheits-Rang
              </label>
              <input
                type="number"
                min={1}
                step={1}
                value={popularityRank}
                onChange={(e) => setPopularityRank(e.target.value)}
                placeholder="z.B. 3"
                className={inputClass}
              />
            </div>

            {/* Pronunciation */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Aussprache</label>
              <input
                type="text"
                value={pronunciation}
                onChange={(e) => setPronunciation(e.target.value)}
                placeholder="z.B. EM-mah"
                className={inputClass}
              />
            </div>

            {/* Nicknames */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Spitznamen
              </label>
              <input
                type="text"
                value={nicknames}
                onChange={(e) => setNicknames(e.target.value)}
                placeholder="Komma-getrennt, z.B. Emmi, Emmy"
                className={inputClass}
              />
              <p className="text-xs text-slate-400 mt-1">Mehrere durch Komma trennen.</p>
            </div>

            {/* Similar Names */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Ähnliche Namen
              </label>
              <input
                type="text"
                value={similarNames}
                onChange={(e) => setSimilarNames(e.target.value)}
                placeholder="Komma-getrennt, z.B. Ella, Emilia, Ema"
                className={inputClass}
              />
              <p className="text-xs text-slate-400 mt-1">Mehrere durch Komma trennen.</p>
            </div>

            {/* Locale */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Sprache *</label>
              <select
                value={locale}
                onChange={(e) => setLocale(e.target.value)}
                required
                className={inputClass}
              >
                <option value="de">Deutsch</option>
                <option value="en">Englisch</option>
              </select>
            </div>

            {/* is_active */}
            <Toggle
              label="Aktiv"
              description="Nur aktive Namen erscheinen in der Nutzer-App."
              checked={isActive}
              onChange={setIsActive}
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 mt-8">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50 font-medium"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 bg-teal-500 hover:bg-teal-600 text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitLabel}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
